import React, {useEffect, memo, useMemo, useState} from 'react';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import {makeStyles} from '@material-ui/core/styles';
import {useTranslation} from 'react-i18next';
import ConversationItemIntents from './ConversationItemIntents/ConversationItemIntents';
import ConversationItemResponse from './ConversationItemResponse/ConversationItemResponse';
import {AnswerRatingButtons} from './AnswerRatingButtons/AnswerRatingButtons';
import AnswerStatusButtons from './AnswerStatusButtons/AnswerStatusButtons';
import ConversationItemComments from './ConversationItemComments/ConversationItemComments';
import ConversationItemEntities from './ConversationItemEntities/ConversationItemEntities';
import ConversationItemContext from './ConversationItemContext/ConversationItemContext';
import ConversationItemLlmFields from './ConversationItemContext/ConversationItemLlmFields';
import {useAnswerContext} from './ConversationItemContext/useAnswerContext';
import ConversationItemNodeExecutions from './ConversationItemNodeExecutions/ConversationItemNodeExecutions';
import ConversationItemAgentTraces from './ConversationItemAgentTraces/ConversationItemAgentTraces';

const useStyles = makeStyles((theme) => ({
    paperLeft: {
        padding: theme.spacing(1),
        backgroundColor: '#d1d1d1',
        marginRight: theme.spacing(12),
        marginBottom: theme.spacing(1),
        display: 'inline-block',
        width: 'auto',
        maxWidth: '100%',
        overflowWrap: 'anywhere'
    }
}));

const getColorBySource = (source) => {
    if (source === 'ALZA-VOCALLS') return {backgroundColor: 'rgb(255 238 252)'};
};

const LLM_ALLOWED_FIELDS = ['UsingAgent', 'PromptID'];

const ConversationItem = (props) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const [userMessageStyle, setUserMessageStyle] = React.useState({});
    const [contextExpanded, setContextExpanded] = useState(false);
    const [text,] = React.useState(props.data['request-text'] ? props.data['request-text'] : '(Chat opened ...)')
    const detailType = props.detail === 'NLP' ? 'NLP' : 'LLM';
    const llmCalls = props.data.llmCalls || (props.data.llmCall ? [props.data.llmCall] : []);
    const {
        data: contextItems,
        isLoading: contextLoading,
        loadError: contextLoadError,
        refetch: refetchContext,
    } = useAnswerContext(props.project, props.data.id, true);

    useEffect(() => {
        if (props.data['request-text']?.includes("BackEndError")) {
            setUserMessageStyle({
                backgroundColor: 'rgba(215,61,61,0.62)'
            })
        } else if (props.data['source'] === 'MANUAL') {
            setUserMessageStyle({
                backgroundColor: 'rgba(255, 165, 0, 0.62)'
            })
        } else if (props.data["request-text"] && !props.data['request-text'].startsWith("Data loaded")) {
            setUserMessageStyle({
                backgroundColor: 'rgba(88,168,19,0.62)'
            })
        }
    }, [props.data])

    useEffect(() => {
        setContextExpanded(false);
    }, [props.data.id]);

    const sharedContextProps = useMemo(() => ({
        contextItems,
        contextLoading,
        contextLoadError,
        onRetryContext: refetchContext,
        openContext: contextExpanded,
        onOpenContextChange: setContextExpanded,
    }), [
        contextItems,
        contextLoading,
        contextLoadError,
        refetchContext,
        contextExpanded,
    ]);

    const renderDetailColumn = () => {
        if (detailType === 'NLP') {
            return (
                <Grid item xs={3} container spacing={2}>
                    <Grid item xs={12}>
                        <ConversationItemIntents intents={props.data.intents}/>
                    </Grid>
                    <Grid item xs={12}>
                        <ConversationItemEntities entities={props.data.entities}/>
                    </Grid>
                    <Grid item xs={12}>
                        <ConversationItemContext
                            detailLayout="nlp"
                            {...sharedContextProps}
                        />
                    </Grid>
                </Grid>
            );
        }

        return (
            <Grid item xs={3} container spacing={2}>
                {llmCalls.map((llmCall, index) => {
                    const llmMetrics = llmCall && (llmCall.responseTimeMillis !== undefined || llmCall.inputTokenCount !== undefined || llmCall.outputTokenCount !== undefined) ? {
                        responseTimeMillis: llmCall.responseTimeMillis,
                        inputTokenCount: llmCall.inputTokenCount,
                        outputTokenCount: llmCall.outputTokenCount
                    } : null;

                    const model = llmCall?.llmModel;
                    const parameters = llmCall?.parameters;
                    const thinkingParams = parameters?.thinking;
                    const thinkingEnabled = !!(thinkingParams && thinkingParams.isEnabled === 'true');

                    let thinkingLabelKey;
                    let thinkingValue;

                    if (thinkingParams && (thinkingParams.thinkingBudget !== undefined || thinkingParams.thinkingLevel !== undefined)) {
                        if (thinkingParams.thinkingBudget !== undefined) {
                            thinkingLabelKey = 'Thinking budget';
                            thinkingValue = thinkingParams.thinkingBudget;
                        } else {
                            thinkingLabelKey = 'Thinking level';
                            thinkingValue = thinkingParams.thinkingLevel;
                        }
                    }

                    const modelInfo = model ? {
                        name: model.name,
                        priceCzkPerInputToken: model.priceCzkPerInputToken,
                        priceCzkPerOutputToken: model.priceCzkPerOutputToken,
                        inputTokenCount: llmMetrics?.inputTokenCount,
                        outputTokenCount: llmMetrics?.outputTokenCount,
                        thinkingEnabled,
                        thinkingLabelKey: thinkingEnabled ? thinkingLabelKey : undefined,
                        thinkingValue: thinkingEnabled ? thinkingValue : undefined,
                        temperature: parameters?.temperature,
                        maxTokens: parameters?.maxTokens
                    } : null;

                    return (
                        <Grid item xs={12} key={index}>
                            <ConversationItemLlmFields
                                contextItems={contextItems}
                                allowedFields={LLM_ALLOWED_FIELDS}
                                llmMetrics={llmMetrics}
                                modelInfo={modelInfo}
                                promptId={llmCall?.promptId}
                                usedAsResponse={llmCall?.usedAsResponse}
                            />
                        </Grid>
                    );
                })}
                <Grid item xs={12}>
                    <ConversationItemContext
                        detailLayout="llm"
                        {...sharedContextProps}
                    />
                </Grid>
            </Grid>
        );
    };

    return (
        <Grid
            container
            direction="row"
            alignItems="flex-start"
            spacing={2}
            style={getColorBySource(props.data['source'])}
        >
            <Grid item xs={6}>
                <div>
                    <Tooltip title={props.data['source'] === 'MANUAL' ? t('manual_message_tooltip') : ""}>
                        <Paper className={classes.paperLeft} style={{...userMessageStyle, display: 'inline-block', maxWidth: '100%'}}>
                            {text}
                        </Paper>
                    </Tooltip>
                    {props.data && props.data.messages ? (
                        <ConversationItemResponse
                            messages={props.data.messages}
                            inputType={props.data.inputType}
                        />
                    ) : null}
                    <ConversationItemNodeExecutions
                        projectId={props.project}
                        conversationId={props.conversationId}
                        answerId={props.data.id}
                    />
                    <ConversationItemAgentTraces
                        projectId={props.project}
                        conversationId={props.conversationId}
                        answerId={props.data.id}
                    />
                </div>
            </Grid>

            {renderDetailColumn()}

            <Grid item xs={3} style={{ alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', justifyContent: 'flex-end' }}>
                        <div>
                            <AnswerRatingButtons
                                project={props.project}
                                conversationId={props.conversationId}
                                answerId={props.data.id}
                                rating={props.data.rating}
                            />
                        </div>
                        <div>
                            <AnswerStatusButtons
                                project={props.project}
                                conversationId={props.conversationId}
                                answerId={props.data.id}
                                status={props.data.status}
                            />
                        </div>
                    </div>
                    <ConversationItemComments
                        project={props.project}
                        conversationId={props.conversationId}
                        answerId={props.data.id}
                        comments={props.data.comments}
                    />
                </div>
            </Grid>
        </Grid>
    );
};

export default memo(ConversationItem);
